import React, { useState } from 'react';
import {
  PhoneCall,
  FileText,
  Loader2,
  AlertCircle,
  Download,
  CheckCircle2,
} from 'lucide-react';

import { API_BASE_URL } from '@/config';

const API_URL = API_BASE_URL;
const API_KEY = import.meta.env.VITE_MCP_API_KEY || '';

interface MassCallsResponse {
  detalle?: string;
}

export function LlamadasMasivas() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState('');

  const readFileAsUTF8 = async (file: File): Promise<File> => {
    const buffer = await file.arrayBuffer();

    try {
      new TextDecoder('utf-8', { fatal: true }).decode(buffer);
      return file;
    } catch {
      const latin1Text = new TextDecoder('windows-1252').decode(buffer);
      const utf8Blob = new Blob([latin1Text], { type: 'text/csv;charset=utf-8;' });
      return new File([utf8Blob], file.name, { type: 'text/csv' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setMensaje('Debes seleccionar un archivo CSV.');
      return;
    }

    setLoading(true);
    setMensaje('');

    const formData = new FormData();
    const safeFile = await readFileAsUTF8(file);
    formData.append('file', safeFile);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'x-api-key': API_KEY,
        },
        body: formData,
      });

      const data: MassCallsResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.detalle || 'Error al agendar llamadas');
      }

      setMensaje(`✅ ${data.detalle || 'Llamadas agendadas'}`);
    } catch (error: unknown) {
      const detail = error instanceof Error ? error.message : 'Error al agendar llamadas';
      setMensaje(`💥 Error: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = `nombre,telefono\nJuan Perez,5512345678\nMaria Lopez,5523456789`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'plantilla-llamadas.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] rounded-xl p-6 mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <PhoneCall className="w-6 h-6 text-[color:var(--acc1)]/90" />
          Llamadas Masivas con Agente IA
        </h2>
      </div>

      <div className="bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] rounded-xl p-6 border border-[color:var(--acc1)]/20">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-between items-center">
            <label className="text-[color:var(--acc1)]/90 text-sm font-medium">Sube tu CSV</label>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="text-[color:var(--acc1)]/90 text-sm flex items-center gap-1 hover:text-white underline"
            >
              <Download className="w-4 h-4" />
              Plantilla CSV
            </button>
          </div>

          <div className="bg-[color:var(--acc2)]/10 rounded-lg px-4 py-3 border border-[color:var(--acc1)]/20">
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-white text-sm focus:outline-none"
            />
            {file && (
              <div className="flex items-center justify-between gap-4 mt-2 text-[color:var(--acc1)]/90 text-sm">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {file.name}
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-red-400 hover:text-red-200 text-xs underline"
                >
                  Eliminar archivo
                </button>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] hover:opacity-90 text-white font-bold py-2.5 px-4 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
            {loading ? 'Agendando...' : 'Agendar Llamadas'}
          </button>

          {mensaje && (
            <div
              className={`text-sm font-medium text-center flex items-center justify-center gap-2 mt-4 ${
                mensaje.startsWith('✅') ? 'text-green-300' : 'text-red-300'
              }`}
            >
              <AlertCircle className="w-4 h-4" />
              {mensaje}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
